import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usersAPI, merchantsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  UserPlus,
  RefreshCw,
  Edit,
  Trash2,
  Shield,
  Mail,
  User,
  Check,
  X,
  Search,
  Filter,
  UserCheck,
  UserX,
  Crown,
  Eye,
  EyeOff,
  Key,
  Send,
  Activity,
  Store,
} from 'lucide-react';
import { toast } from 'sonner';

const roleConfig = {
  super_admin: { 
    label: 'Супер Админ', 
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    icon: Crown,
    description: 'Полный доступ ко всем функциям'
  },
  admin: { 
    label: 'Админ', 
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    icon: Shield,
    description: 'Управление пользователями и настройками'
  },
  accountant: { 
    label: 'Бухгалтерия', 
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: Activity,
    description: 'Доступ к финансовым отчетам'
  },
  analyst: { 
    label: 'Аналитик', 
    color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    icon: Activity,
    description: 'Просмотр аналитики и отчетов'
  },
  aml_officer: { 
    label: 'AML', 
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    icon: Shield,
    description: 'Работа с AML алертами'
  },
  lawyer: { 
    label: 'Юрист', 
    color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    icon: User,
    description: 'Доступ к юридическим документам'
  },
  manager: { 
    label: 'Менеджер', 
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    icon: User,
    description: 'Управление мерчантами'
  },
  user: { 
    label: 'Пользователь', 
    color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    icon: User,
    description: 'Базовый доступ'
  },
  merchant: { 
    label: 'Мерчант', 
    color: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    icon: Store,
    description: 'Доступ к личному кабинету мерчанта'
  },
};

const RoleBadge = ({ role }) => {
  const config = roleConfig[role] || roleConfig.user;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className={`bg-slate-800/50 border-slate-700/50`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const UsersPage = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user',
    telegram_id: '',
    merchant_id: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [merchants, setMerchants] = useState([]);

  const canWrite = hasPermission('users', 'write');
  const canDelete = hasPermission('users', 'delete');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersAPI.list();
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const response = await merchantsAPI.list({ page_size: 500 });
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Failed to fetch merchants:', err);
    }
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = !search || 
        user.name?.toLowerCase().includes(search.toLowerCase()) ||
        user.email?.toLowerCase().includes(search.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => u.is_active).length;
    const inactive = total - active;
    const admins = users.filter(u => ['super_admin', 'admin'].includes(u.role)).length;
    return { total, active, inactive, admins };
  }, [users]);

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'user',
      telegram_id: '',
      merchant_id: '',
      is_active: true,
    });
    setShowPassword(false);
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      telegram_id: user.telegram_id?.toString() || '',
      merchant_id: user.merchant_id?.toString() || '',
      is_active: user.is_active,
    });
    setShowPassword(false);
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Введите имя пользователя');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Введите email');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('Введите пароль');
      return;
    }
    if (formData.role === 'merchant' && !formData.merchant_id) {
      toast.error('Выберите мерчанта для роли "Мерчант"');
      return;
    }
    
    try {
      setSaving(true);
      const data = {
        ...formData,
        telegram_id: formData.telegram_id ? parseInt(formData.telegram_id) : null,
        merchant_id: formData.merchant_id ? parseInt(formData.merchant_id) : null,
      };
      
      if (editingUser) {
        if (!data.password) delete data.password;
        await usersAPI.update(editingUser.id, data);
        toast.success('Пользователь обновлен');
      } else {
        await usersAPI.create(data);
        toast.success('Пользователь создан');
      }
      
      setShowDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    
    try {
      await usersAPI.delete(deleteUser.id);
      toast.success('Пользователь удален');
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await usersAPI.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Пользователь заблокирован' : 'Пользователь активирован');
      fetchUsers();
    } catch (err) {
      toast.error('Ошибка изменения статуса');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('users.title')}</h1>
          <p className="text-slate-400 mt-1">Управление сотрудниками системы</p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('common.create')}
            </Button>
          )}
          <Button
            onClick={fetchUsers}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title={t('common.total')}
          value={stats.total}
          icon={Users}
          color="bg-blue-500/20 text-blue-400"
        />
        <StatCard
          title="Активных"
          value={stats.active}
          icon={UserCheck}
          color="bg-emerald-500/20 text-emerald-400"
        />
        <StatCard
          title="Заблокированных"
          value={stats.inactive}
          icon={UserX}
          color="bg-red-500/20 text-red-400"
        />
        <StatCard
          title="Администраторов"
          value={stats.admins}
          icon={Crown}
          color="bg-purple-500/20 text-purple-400"
        />
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Поиск по имени или email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full md:w-48 bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder="Все роли" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все роли</SelectItem>
                {Object.entries(roleConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40 bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                <SelectItem value="active">Активные</SelectItem>
                <SelectItem value="inactive">Заблокированные</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
          {error}
        </div>
      )}

      {/* Users Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Users className="w-12 h-12 mb-4 opacity-50" />
              <p>{search || roleFilter !== 'all' || statusFilter !== 'all' ? 'Пользователи не найдены по заданным критериям' : 'Пользователи не найдены'}</p>
              {canWrite && !search && roleFilter === 'all' && statusFilter === 'all' && (
                <Button onClick={openCreateDialog} className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Создать первого пользователя
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('audit.user')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('auth.email')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('users.role')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Telegram</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.role === 'super_admin' ? 'bg-gradient-to-br from-red-500 to-orange-500' :
                            user.role === 'admin' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                            'bg-gradient-to-br from-emerald-500 to-cyan-500'
                          }`}>
                            <span className="text-white font-medium text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <span className="text-white font-medium block">{user.name}</span>
                            {user.id === currentUser?.id && (
                              <span className="text-xs text-emerald-400">Вы</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300">{user.email}</span>
                      </td>
                      <td className="p-4">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="p-4">
                        {user.telegram_id ? (
                          <span className="text-slate-300 text-sm font-mono">{user.telegram_id}</span>
                        ) : (
                          <span className="text-slate-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.is_active ? (
                          <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            <Check className="w-3 h-3 mr-1" />
                            {t('common.active')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                            <X className="w-3 h-3 mr-1" />
                            Заблокирован
                          </Badge>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canWrite && user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleUserStatus(user)}
                              className={`${user.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}`}
                              title={user.is_active ? 'Заблокировать' : 'Активировать'}
                              data-testid={`toggle-status-btn-${user.id}`}
                            >
                              {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            </Button>
                          )}
                          {canWrite && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              className="text-slate-400 hover:text-white"
                              data-testid={`edit-user-btn-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteUser(user)}
                              className="text-slate-400 hover:text-red-400"
                              data-testid={`delete-user-btn-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Results count */}
          {!loading && filteredUsers.length > 0 && (
            <div className="p-4 border-t border-slate-700 text-sm text-slate-400">
              Показано {filteredUsers.length} из {users.length} пользователей
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingUser ? <Edit className="w-5 h-5 text-emerald-500" /> : <UserPlus className="w-5 h-5 text-emerald-500" />}
              {editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingUser ? `Редактирование: ${editingUser.name}` : 'Заполните данные нового пользователя'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Имя *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Иван Иванов"
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                  data-testid="user-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@oxypay.kz"
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                  disabled={!!editingUser}
                  data-testid="user-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">
                Пароль {editingUser ? '(оставьте пустым, чтобы не менять)' : '*'}
              </Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-slate-700/50 border-slate-600 text-white"
                  data-testid="user-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">{t('users.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {roleConfig[formData.role]?.description}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Telegram ID</Label>
              <div className="relative">
                <Send className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="number"
                  value={formData.telegram_id}
                  onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
                  placeholder="123456789"
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                  data-testid="user-telegram-input"
                />
              </div>
              <p className="text-xs text-slate-500">Для получения уведомлений в Telegram</p>
            </div>

            {formData.role === 'merchant' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Привязка к мерчанту *</Label>
                <Select
                  value={formData.merchant_id}
                  onValueChange={(value) => setFormData({ ...formData, merchant_id: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="user-merchant-select">
                    <SelectValue placeholder="Выберите мерчанта" />
                  </SelectTrigger>
                  <SelectContent>
                    {merchants.map((m) => (
                      <SelectItem key={m.id} value={m.id.toString()}>
                        #{m.id} - {m.name || m.company_name || 'Без названия'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Мерчант получит доступ к терминалам этого аккаунта</p>
              </div>
            )}

            {editingUser && (
              <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div>
                  <Label className="text-slate-300">{t('merchants.accountStatus')}</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.is_active ? 'Пользователь активен' : 'Пользователь заблокирован'}
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="user-status-switch"
                />
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={saving}
                data-testid="save-user-btn"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                {editingUser ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Удалить пользователя?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Вы действительно хотите удалить пользователя <strong className="text-white">{deleteUser?.name}</strong>? 
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-700">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
